import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Button } from './Button';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange?: (limit: number) => void;
  showItemsPerPage?: boolean;
  itemsPerPageOptions?: number[];
  translations?: {
    showing?: string;
    to?: string;
    of?: string;
    results?: string;
    itemsPerPage?: string;
    previous?: string;
    next?: string;
    firstPage?: string;
    lastPage?: string;
    goToPage?: string;
  };
}

export const Pagination = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  showItemsPerPage = true,
  itemsPerPageOptions = [10, 20, 50, 100],
  translations = {},
}: PaginationProps) => {
  // Default translations
  const t = {
    showing: translations.showing || 'Showing',
    to: translations.to || 'to',
    of: translations.of || 'of',
    results: translations.results || 'results',
    itemsPerPage: translations.itemsPerPage || 'Items per page:',
    previous: translations.previous || 'Previous',
    next: translations.next || 'Next',
    firstPage: translations.firstPage || 'First page',
    lastPage: translations.lastPage || 'Last page',
    goToPage: translations.goToPage || 'Go to page',
  };
  // Calculate the range of items being displayed
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxPagesToShow = 7; // Show more pages for better UX

    if (totalPages === 0) {
      return [];
    }

    if (totalPages <= maxPagesToShow) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      // Calculate the range of pages to show around current page
      let startPage = Math.max(2, currentPage - 2);
      let endPage = Math.min(totalPages - 1, currentPage + 2);

      // Adjust range if we're near the start
      if (currentPage <= 4) {
        endPage = Math.min(totalPages - 1, 5);
      }

      // Adjust range if we're near the end
      if (currentPage >= totalPages - 3) {
        startPage = Math.max(2, totalPages - 4);
      }

      // Add ellipsis after first page if needed
      if (startPage > 2) {
        pages.push('...');
      }

      // Add pages in range
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      // Add ellipsis before last page if needed
      if (endPage < totalPages - 1) {
        pages.push('...');
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  if (totalPages === 0) {
    return null;
  }

  return (
    <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-t border-slate-200 bg-white px-4 py-4 rounded-b-lg'>
      {/* Items info and per-page selector */}
      <div className='flex flex-col sm:flex-row sm:items-center gap-3 text-sm text-slate-700'>
        <span className='whitespace-nowrap'>
          {t.showing} <span className='font-semibold text-slate-900'>{startItem}</span> {t.to}{' '}
          <span className='font-semibold text-slate-900'>{endItem}</span> {t.of}{' '}
          <span className='font-semibold text-slate-900'>{totalItems}</span> {t.results}
        </span>

        {showItemsPerPage && onItemsPerPageChange && (
          <div className='flex items-center gap-2'>
            <label htmlFor='items-per-page' className='whitespace-nowrap font-medium'>
              {t.itemsPerPage}
            </label>
            <select
              id='items-per-page'
              value={itemsPerPage}
              onChange={e => onItemsPerPageChange(Number(e.target.value))}
              className='rounded-lg border-2 border-slate-300 py-1.5 px-3 text-sm font-medium focus:border-primary focus:outline-none focus:ring-0 min-w-[70px] bg-white hover:border-slate-400 transition-colors cursor-pointer'
            >
              {itemsPerPageOptions.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Pagination controls */}
      <div className='flex items-center justify-center sm:justify-end gap-2'>
        {/* First page button */}
        <Button
          variant='outline'
          size='sm'
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          className={`hidden sm:flex hover:bg-secondary hover:border-primary/40 transition-colors focus:outline-none focus:ring-0 ${
            currentPage === 1 ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'
          }`}
          aria-label={t.firstPage}
          title={t.firstPage}
        >
          <ChevronsLeft className='h-4 w-4' />
        </Button>

        {/* Previous page button */}
        <Button
          variant='outline'
          size='sm'
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`hover:bg-secondary hover:border-primary/40 transition-colors focus:outline-none focus:ring-0 ${
            currentPage === 1 ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'
          }`}
          aria-label={t.previous}
          title={t.previous}
        >
          <ChevronLeft className='h-4 w-4' />
          <span className='hidden md:inline ml-1'>{t.previous}</span>
        </Button>

        {/* Page numbers */}
        <div className='flex items-center gap-1'>
          {pageNumbers.map((page, index) => {
            if (page === '...') {
              return (
                <span key={`ellipsis-${index}`} className='px-3 py-2 text-slate-400 font-medium select-none'>
                  ...
                </span>
              );
            }

            const pageNumber = page as number;
            const isCurrentPage = pageNumber === currentPage;

            // On mobile, show current page, first, last, and one adjacent page
            const isMobileVisible =
              pageNumber === currentPage ||
              pageNumber === 1 ||
              pageNumber === totalPages ||
              Math.abs(pageNumber - currentPage) === 1;

            return (
              <Button
                key={pageNumber}
                variant={isCurrentPage ? 'default' : 'outline'}
                size='sm'
                onClick={() => !isCurrentPage && onPageChange(pageNumber)}
                className={`min-w-[40px] font-semibold transition-all focus:outline-none focus:ring-0 ${
                  !isMobileVisible ? 'hidden sm:flex' : ''
                } ${
                  isCurrentPage
                    ? 'bg-primary hover:bg-primary text-white shadow-none cursor-default'
                    : 'hover:bg-secondary hover:border-primary/40 hover:text-primary cursor-pointer active:scale-95'
                }`}
                aria-label={`${isCurrentPage ? 'Current page, ' : ''}${t.goToPage} ${pageNumber}`}
                aria-current={isCurrentPage ? 'page' : undefined}
                title={`${t.goToPage} ${pageNumber}`}
              >
                {pageNumber}
              </Button>
            );
          })}
        </div>

        {/* Next page button */}
        <Button
          variant='outline'
          size='sm'
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`hover:bg-secondary hover:border-primary/40 transition-colors focus:outline-none focus:ring-0 ${
            currentPage === totalPages ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'
          }`}
          aria-label={t.next}
          title={t.next}
        >
          <span className='hidden md:inline mr-1'>{t.next}</span>
          <ChevronRight className='h-4 w-4' />
        </Button>

        {/* Last page button */}
        <Button
          variant='outline'
          size='sm'
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          className={`hidden sm:flex hover:bg-secondary hover:border-primary/40 transition-colors focus:outline-none focus:ring-0 ${
            currentPage === totalPages ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'
          }`}
          aria-label={t.lastPage}
          title={t.lastPage}
        >
          <ChevronsRight className='h-4 w-4' />
        </Button>
      </div>
    </div>
  );
};
